import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import TopBarRight from '../common/TopBarRight';
import '../../styles/admin/AdminLayout.css';

const GEOGUESSER_PATHS = [
  '/admin/geoguesser',
  '/admin/geoguesser-maps',
  '/admin/geoguesser-floors',
  '/admin/level-submissions',
  '/admin/levels',
  '/admin/game-modes',
  '/admin/daily-challenge',
  '/admin/sessions',
  '/admin/deprecations',
  '/admin/geoguesser-data',
];

const SIDEBAR_SECTIONS = {
  main: [
    { path: '/admin', label: 'Dashboard', icon: 'dashboard' },
    { path: '/admin/badges', label: 'Badges', icon: 'workspace_premium' },
    { path: '/admin/games', label: 'Games', icon: 'sports_esports' },
    { path: '/admin/activity', label: 'Activity Log', icon: 'history' },
    { path: '/admin/feedback', label: 'Feedback', icon: 'feedback' },
    { path: '/admin/cache-stats', label: 'Cache Stats', icon: 'speed' },
  ],
  strategies: [
    { path: '/admin/maps', label: 'Strategy Maps', icon: 'map' },
    { path: '/admin/strategies', label: 'Strategies', icon: 'edit_note' },
  ],
  geoguesser: [
    { path: '/admin/geoguesser', label: 'Overview', icon: 'public' },
    { path: '/admin/geoguesser-maps', label: 'Maps', icon: 'explore' },
    { path: '/admin/geoguesser-floors', label: 'Floors', icon: 'layers' },
    { path: '/admin/level-submissions', label: 'Level Suggestions', icon: 'task_alt' },
    { path: '/admin/levels', label: 'Levels', icon: 'place' },
    { path: '/admin/game-modes', label: 'Game Modes', icon: 'tune' },
    { path: '/admin/daily-challenge', label: 'Daily Challenge', icon: 'today' },
    { path: '/admin/sessions', label: 'Sessions', icon: 'groups' },
    { path: '/admin/deprecations', label: 'Deprecations', icon: 'warning' },
    { path: '/admin/geoguesser-data', label: 'Import / Export', icon: 'sync_alt' },
  ],
};

const AdminLayout = ({ children }) => {
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [openStrategies, setOpenStrategies] = useState(false);
  const [openGeoguesser, setOpenGeoguesser] = useState(false);

  useEffect(() => {
    if (GEOGUESSER_PATHS.some((p) => location.pathname === p)) {
      setOpenGeoguesser(true);
      setOpenStrategies(false);
    } else if (location.pathname === '/admin/maps' || location.pathname === '/admin/strategies') {
      setOpenStrategies(true);
      setOpenGeoguesser(false);
    }
  }, [location.pathname]);

  const isActive = (path) => location.pathname === path || (path !== '/admin' && location.pathname.startsWith(path));

  const toggleStrategies = () => {
    setOpenGeoguesser(false);
    setOpenStrategies((prev) => !prev);
  };

  const toggleGeoguesser = () => {
    setOpenStrategies(false);
    setOpenGeoguesser((prev) => !prev);
  };

  return (
    <div className="admin-layout">
      <aside className={`admin-sidebar ${sidebarOpen ? 'open' : 'closed'}`}>
        <div className="admin-sidebar-header">
          <Link to="/admin" className="admin-sidebar-brand" aria-label="Admin home">
            <img
              className="admin-sidebar-brand-logo"
              src={`${process.env.PUBLIC_URL || ''}/stratguys-favicon.jpg`}
              alt=""
              width="32"
              height="32"
            />
            {sidebarOpen && <span className="admin-sidebar-brand-text">Admin</span>}
          </Link>
        </div>

        <nav className="admin-sidebar-nav sg-scroll-y">
          <div className="admin-nav-section">
            {sidebarOpen && <span className="admin-nav-section-label">Main</span>}
            {SIDEBAR_SECTIONS.main.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                className={`admin-nav-item ${isActive(item.path) ? 'active' : ''}`}
                title={!sidebarOpen ? item.label : undefined}
              >
                <span className="admin-nav-icon material-symbols-outlined">{item.icon}</span>
                {sidebarOpen && <span className="admin-nav-label">{item.label}</span>}
              </Link>
            ))}
          </div>

          <div className="admin-nav-section">
            <button
              type="button"
              className={`admin-nav-group-trigger ${openStrategies ? 'open' : ''}`}
              onClick={toggleStrategies}
              aria-expanded={openStrategies}
              title={!sidebarOpen ? 'Strategies' : undefined}
            >
              <span className="admin-nav-icon material-symbols-outlined">strategy</span>
              {sidebarOpen && (
                <>
                  <span className="admin-nav-label">Strategies</span>
                  <span className="admin-nav-chevron material-symbols-outlined">
                    {openStrategies ? 'expand_less' : 'expand_more'}
                  </span>
                </>
              )}
            </button>
            {openStrategies && (
              <div className="admin-nav-group-children">
                {SIDEBAR_SECTIONS.strategies.map((item) => (
                  <Link
                    key={item.path}
                    to={item.path}
                    className={`admin-nav-item child ${isActive(item.path) ? 'active' : ''}`}
                    title={!sidebarOpen ? item.label : undefined}
                  >
                    <span className="admin-nav-icon material-symbols-outlined">{item.icon}</span>
                    {sidebarOpen && <span className="admin-nav-label">{item.label}</span>}
                  </Link>
                ))}
              </div>
            )}
          </div>

          <div className="admin-nav-section">
            <button
              type="button"
              className={`admin-nav-group-trigger ${openGeoguesser ? 'open' : ''}`}
              onClick={toggleGeoguesser}
              aria-expanded={openGeoguesser}
              title={!sidebarOpen ? 'Geoguesser' : undefined}
            >
              <span className="admin-nav-icon material-symbols-outlined">public</span>
              {sidebarOpen && (
                <>
                  <span className="admin-nav-label">Geoguesser</span>
                  <span className="admin-nav-chevron material-symbols-outlined">
                    {openGeoguesser ? 'expand_less' : 'expand_more'}
                  </span>
                </>
              )}
            </button>
            {openGeoguesser && (
              <div className="admin-nav-group-children">
                {SIDEBAR_SECTIONS.geoguesser.map((item) => (
                  <Link
                    key={item.path}
                    to={item.path}
                    className={`admin-nav-item child ${isActive(item.path) ? 'active' : ''}`}
                    title={!sidebarOpen ? item.label : undefined}
                  >
                    <span className="admin-nav-icon material-symbols-outlined">{item.icon}</span>
                    {sidebarOpen && <span className="admin-nav-label">{item.label}</span>}
                  </Link>
                ))}
              </div>
            )}
          </div>
        </nav>
      </aside>

      <main className="admin-main">
        <header className="admin-header">
          <div className="admin-header-left">
            <button
              type="button"
              className="btn icon admin-sidebar-toggle"
              onClick={() => setSidebarOpen(!sidebarOpen)}
              aria-label={sidebarOpen ? 'Collapse sidebar' : 'Expand sidebar'}
            >
              <span className="material-symbols-outlined">{sidebarOpen ? 'menu_open' : 'menu'}</span>
            </button>
            <h1 className="admin-header-title">Administration</h1>
          </div>
          <div className="admin-header-right">
            <Link to="/" className="btn btn-secondary admin-view-site-btn" title="Leave admin and view the main site">
              <span className="material-symbols-outlined">open_in_new</span>
              <span className="admin-view-site-label">View site</span>
            </Link>
            <TopBarRight />
          </div>
        </header>

        <div className="admin-content sg-scroll-y">
          {children}
        </div>
      </main>
    </div>
  );
};

export default AdminLayout;
